import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Checkbox, message } from 'antd';
import { useSelector } from 'react-redux';
import useProjectId from '@hooks/useProjectId';
import { DEFALUT_PERMISSION_SECTION } from '@model';
import {
  getModuleEnableChat,
  getModuleRowId,
  getModuleRowValue,
} from '../api/moduleRowFields';
import {
  lookupEnableChatInServerMap,
  saveModuleEnableChatFlags,
} from '../api/gatewayProjectEnableChat';
import { useModuleEnableChatServerMap } from '../hooks/useModuleEnableChatServerMap';
import './chatAiSettingsTab.scss';

/**
 * @param {{
 *   customizeModule?: Array<{ module?: string, value?: string, type?: string, enableChat?: boolean }> | null,
 *   getModuleForm?: () => import('antd').FormInstance | undefined,
 *   onSaved?: () => void,
 *   operatorName?: string,
 * }} props
 */
export default function ChatAiSettingsTab({
  customizeModule,
  getModuleForm,
  onSaved,
  operatorName: operatorNameProp,
}) {
  const projectId = useProjectId();
  const operatorNameFromStore = useSelector((state) => {
    const u = state.login?.userInfo;
    if (!u) {
      return '';
    }
    return (
      u.name ||
      [u.given_name, u.family_name].filter(Boolean).join(' ').trim() ||
      ''
    );
  });
  const operatorName = (operatorNameProp || operatorNameFromStore || '').trim();

  const { serverModuleMap } = useModuleEnableChatServerMap(projectId);

  const [pendingOverrides, setPendingOverrides] = useState({});
  const [saving, setSaving] = useState(false);
  const pendingOverridesSyncRef = useRef(pendingOverrides);
  pendingOverridesSyncRef.current = pendingOverrides;

  useEffect(() => {
    setPendingOverrides({});
  }, [projectId]);

  const moduleRows = useMemo(() => {
    if (!Array.isArray(customizeModule)) {
      return [];
    }
    return customizeModule.filter((row) => {
      const v = getModuleRowValue(row);
      return row && v && v !== DEFALUT_PERMISSION_SECTION.PEOJECT_LIST;
    });
  }, [customizeModule]);

  const isModuleChecked = useCallback(
    (row, stableKey) => {
      if (Object.prototype.hasOwnProperty.call(pendingOverrides, stableKey)) {
        return pendingOverrides[stableKey];
      }
      const val = getModuleRowValue(row);
      const fromServer = lookupEnableChatInServerMap(serverModuleMap, val);
      if (typeof fromServer === 'boolean') {
        return fromServer;
      }
      const c = row?.enableChat ?? row?.EnableChat;
      return typeof c === 'boolean' ? c : false;
    },
    [pendingOverrides, serverModuleMap],
  );

  const hasPendingModuleChanges = useMemo(
    () => Object.keys(pendingOverrides).length > 0,
    [pendingOverrides],
  );

  const flushSave = useCallback(async () => {
    if (!projectId) {
      message.warning('Unable to save: missing project.');
      return;
    }
    if (!operatorName) {
      message.warning('Unable to save: missing operator name.');
      return;
    }
    const toSave = pendingOverridesSyncRef.current;
    if (Object.keys(toSave).length === 0) {
      message.info('No module checkbox changes to save.');
      return;
    }
    setSaving(true);
    try {
      const result = await saveModuleEnableChatFlags(
        projectId,
        { pendingOverrides: toSave },
        operatorName,
      );
      if (result.success) {
        message.success('Module AI Chat settings saved.');
        setPendingOverrides({});
        // Do not refetch here: GET may omit or lag `enableChat` and would overwrite
        // the map already applied via CHAT_AI_ENABLE_CHAT_SERVER_CHANGED.
        onSaved?.();
      } else if (result.error) {
        message.error(result.error);
      }
    } finally {
      setSaving(false);
    }
  }, [onSaved, operatorName, projectId]);

  const stableKeyForRow = useCallback((row) => {
    const rid = getModuleRowId(row);
    if (rid != null && rid !== '') {
      return String(rid);
    }
    return String(getModuleRowValue(row) || '');
  }, []);

  const syncEnableChatToModuleForm = useCallback(
    (stableKey, enableChat) => {
      const form = getModuleForm?.();
      if (!form) {
        return;
      }
      const current = form.getFieldsValue()?.customizeModule;
      if (!Array.isArray(current)) {
        return;
      }
      const next = current.map((row) => {
        if (!row) {
          return row;
        }
        if (stableKeyForRow(row) !== stableKey) {
          return row;
        }
        return { ...row, enableChat, EnableChat: enableChat };
      });
      form.setFieldsValue({ customizeModule: next });
    },
    [getModuleForm, stableKeyForRow],
  );

  const handleModuleCheckboxChange = (stableKey, checked) => {
    setPendingOverrides((prev) => ({ ...prev, [stableKey]: checked }));
    syncEnableChatToModuleForm(stableKey, checked);
  };

  const onCancelModuleDraft = () => {
    const keys = Object.keys(pendingOverrides);
    const form = keys.length ? getModuleForm?.() : null;
    if (form) {
      const current = form.getFieldsValue()?.customizeModule;
      if (Array.isArray(current)) {
        const next = current.map((row) => {
          if (!row) {
            return row;
          }
          const sk = stableKeyForRow(row);
          if (!keys.includes(sk)) {
            return row;
          }
          const val = getModuleRowValue(row);
          const fromServer = lookupEnableChatInServerMap(serverModuleMap, val);
          let base =
            typeof fromServer === 'boolean'
              ? fromServer
              : getModuleEnableChat(
                  customizeModule?.find((r) => stableKeyForRow(r) === sk),
                );
          if (typeof base !== 'boolean') {
            base = false;
          }
          return { ...row, enableChat: base, EnableChat: base };
        });
        form.setFieldsValue({ customizeModule: next });
      }
    }
    setPendingOverrides({});
  };

  return (
    <div className="chat-ai-settings-tab page-wrapper">
      <p className="chat-ai-settings-tab__hint">
        Turn AI Chat on or off per module. Changes are saved on the server when
        you click Save. The assistant still respects route rules, chatbot
        visibility, and API configuration.
      </p>

      <div className="chat-ai-settings-tab__modules">
        <h3 className="chat-ai-settings-tab__modules-title">Modules</h3>
        <p className="chat-ai-settings-tab__hint chat-ai-settings-tab__modules-intro">
          Checked modules can show the AI assistant on supported pages when other
          gates pass. Unchecked modules will not show the assistant.
        </p>
        {moduleRows.length === 0 ? (
          <p className="chat-ai-settings-tab__empty">
            {customizeModule == null
              ? 'No modules loaded yet. Open the Modules tab or refresh if the list should appear.'
              : 'No modules configured for this project.'}
          </p>
        ) : (
          <>
            <ul className="chat-ai-settings-tab__module-list">
              {moduleRows.map((row, index) => {
                const stableKey = stableKeyForRow(row);
                const label =
                  row.module || row.Module || getModuleRowValue(row);
                const typeHint = row.type ? ` (${row.type})` : '';
                return (
                  <li
                    key={`${stableKey}-${index}`}
                    className="chat-ai-settings-tab__module-item"
                  >
                    <Checkbox
                      checked={isModuleChecked(row, stableKey)}
                      onChange={(e) =>
                        handleModuleCheckboxChange(stableKey, e.target.checked)
                      }
                      aria-label={`AI Chat for ${label}`}
                    >
                      <span className="chat-ai-settings-tab__module-label">
                        {label}
                        {typeHint}
                      </span>
                    </Checkbox>
                  </li>
                );
              })}
            </ul>
            <div className="chat-ai-settings-tab__module-actions">
              <Button
                type="primary"
                onClick={flushSave}
                disabled={!hasPendingModuleChanges || saving}
                loading={saving}
              >
                Save module settings
              </Button>
              <Button
                onClick={onCancelModuleDraft}
                disabled={!hasPendingModuleChanges || saving}
              >
                Cancel
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
